import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export default function RegisterPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    email: '',
    phone_number: '',
    password: '',
    confirm_password: '',
    access_level: 'User'
  });
  const [usernameMode, setUsernameMode] = useState('auto'); // 'auto' ou 'manual'
  const [previewUsername, setPreviewUsername] = useState('');
  const [manualUsername, setManualUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Fonction pour générer un username prévisualisé
  const generatePreviewUsername = (firstName, lastName) => {
    const cleanFirstName = firstName ? firstName.trim().replace(/[^a-zA-Z0-9]/g, '').toLowerCase() : '';
    const cleanLastName = lastName ? lastName.trim().replace(/[^a-zA-Z0-9]/g, '').toLowerCase() : '';
    
    // Si aucun des deux n'est rempli, pas de prévisualisation
    if (!cleanFirstName && !cleanLastName) return '';
    
    // Si seulement le prénom : utiliser "caisse" comme nom
    if (cleanFirstName && !cleanLastName) {
      const randomDigits = Math.floor(10 + Math.random() * 90);
      return cleanFirstName + 'caisse' + randomDigits;
    }
    
    // Si seulement le nom : utiliser le nom seul
    if (!cleanFirstName && cleanLastName) {
      const randomDigits = Math.floor(10 + Math.random() * 90);
      return cleanLastName + randomDigits;
    }
    
    // Si les deux : prénom + nom
    const randomDigits = Math.floor(10 + Math.random() * 90);
    return cleanFirstName + cleanLastName + randomDigits;
  };

  // Mettre à jour le username prévisualisé quand first_name ou last_name change
  useEffect(() => {
    if (usernameMode === 'auto' && (formData.first_name || formData.last_name)) {
      const preview = generatePreviewUsername(formData.first_name, formData.last_name);
      setPreviewUsername(preview);
    } else {
      setPreviewUsername('');
    }
  }, [formData.first_name, formData.last_name, usernameMode]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validation des champs requis (au moins prénom OU nom, plus email et password)
    if ((!formData.first_name && !formData.last_name) || !formData.email || !formData.password) {
      setError('Au moins le prénom ou le nom, plus l\'email et le mot de passe sont requis');
      setLoading(false);
      return;
    }

    // Validation mot de passe minimum 8 caractères
    if (formData.password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      setLoading(false);
      return;
    }

    // Vérifier que les mots de passe correspondent
    if (formData.password !== formData.confirm_password) {
      setError('Les mots de passe ne correspondent pas');
      setLoading(false);
      return;
    }

    // Validation username si mode manuel
    if (usernameMode === 'manual' && !manualUsername.trim()) {
      setError('Veuillez saisir un nom d\'utilisateur');
      setLoading(false);
      return;
    }

    if (usernameMode === 'manual' && manualUsername.length < 3) {
      setError('Le nom d\'utilisateur doit contenir au moins 3 caractères');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/auth/regst', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          first_name: formData.first_name,
          last_name: formData.last_name,
          date_of_birth: formData.date_of_birth || null,
          email: formData.email,
          phone_number: formData.phone_number,
          password: formData.password,
          confirm_password: formData.confirm_password,
          access_level: formData.access_level,
          username: usernameMode === 'manual' ? manualUsername.trim() : null
        })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Inscription réussie ! Connexion en cours...');
        const user = {
          ...data.user,
          loginMethod: 'username',
          token: data.token
        };
        login(user);
        // Rediriger après 1 seconde
        setTimeout(() => {
          navigate('/clients', { replace: true });
        }, 1000);
      } else {
        setError(data.error || 'Erreur lors de l\'inscription');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style jsx global>{`
        body, html {
          height: 100vh;
          overflow: auto;
          background: rgb(187, 187, 187);
        }
        
        .register-page {
          background: transparent;
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          padding: 2rem 1rem;
        }
        
        .register-container {
          background: #ffffff;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          padding: 2rem;
          max-width: 800px;
          width: 100%;
          position: relative;
          z-index: 10;
        }
        
        .register-header {
          text-align: center;
          margin-bottom: 2rem;
        }
        
        .register-header-title {
          background: linear-gradient(135deg, #0b5796 0%, #0d6efd 100%);
          color: #ffffff;
          padding: 0.75rem 1rem;
          border-radius: 8px;
          margin-bottom: 1rem;
          font-size: 1.5rem;
          font-weight: 400;
        }
        
        .register-logo {
          width: 120px;
          height: 80px;
          margin: 0 auto 1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
        }
        
        .register-logo img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          display: block;
        }
        
        .register-subtitle {
          color: #6c757d;
          font-size: 0.9rem;
          font-weight: 400;
          margin-top: 0.5rem;
        }
        
        .modern-input-group {
          position: relative;
          margin-bottom: 1rem;
        }
        
        .modern-input {
          width: 100%;
          padding: 10px 12px 10px 40px;
          border: 1px solid #e9ecef;
          border-radius: 6px;
          font-size: 0.9rem;
          transition: all 0.2s ease;
          background: #ffffff;
          color: #495057;
        }
        
        .modern-input:focus {
          outline: none;
          border-color: #0b5796;
          box-shadow: 0 0 0 2px rgba(11, 87, 150, 0.1);
        }
        
        .modern-input-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #6c757d;
          font-size: 1rem;
          z-index: 2;
        }
        
        .modern-register-btn {
          width: 100%;
          background: #2E7D32;
          border: none;
          color: white;
          border-radius: 6px;
          padding: 10px 20px;
          font-weight: 400;
          font-size: 0.95rem;
          transition: all 0.2s ease;
          margin-top: 0.5rem;
        }
        
        .modern-register-btn:hover {
          background: #256528;
          transform: translateY(-1px);
          box-shadow: 0 2px 6px rgba(46, 125, 50, 0.3);
        }
        
        .modern-register-btn:disabled {
          opacity: 0.7;
          transform: none;
          cursor: not-allowed;
        }
        
        .modern-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid #f3f3f3;
          border-top: 2px solid #ffffff;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          display: inline-block;
          margin-right: 8px;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .error-alert {
          background: #dc3545;
          color: white;
          border: none;
          border-radius: 6px;
          padding: 10px 15px;
          margin-bottom: 1rem;
          font-weight: 400;
          font-size: 0.85rem;
        }
        
        .success-alert {
          background: #28a745;
          color: white;
          border: none;
          border-radius: 6px;
          padding: 10px 15px;
          margin-bottom: 1rem;
          font-weight: 400;
          font-size: 0.85rem;
        }
        
        .register-footer {
          text-align: center;
          margin-top: 1rem;
          color: #6c757d;
          font-size: 0.85rem;
        }
        
        .register-footer a {
          color: #0b5796;
          text-decoration: none;
          font-weight: 400;
        }
        
        .register-footer a:hover {
          text-decoration: underline;
        }

        .form-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1rem;
          margin-bottom: 1rem;
        }

        .required-field {
          color: #dc3545;
        }
        
        @media (max-width: 768px) {
          .register-container {
            padding: 1.25rem;
            margin: 0.5rem;
            max-width: 100%;
          }
          
          .register-header-title {
            font-size: 1.3rem;
            padding: 0.6rem 0.8rem;
          }
          
          .register-logo {
            width: 100px;
            height: 70px;
          }
          
          .register-subtitle {
            font-size: 0.85rem;
          }

          .form-row {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 480px) {
          .register-container {
            padding: 1rem;
          }
          
          .register-header-title {
            font-size: 1.2rem;
            padding: 0.5rem 0.75rem;
          }
          
          .register-logo {
            width: 90px;
            height: 60px;
          }
        }
      `}</style>

      <div className="register-page">
        <div className="register-container">
          {/* Header */}
          <div className="register-header">
            <div className="register-logo">
              <img src="/logo.png" alt="Caisse CGM Logo" />
            </div>
            <h1 className="register-header-title">Caisse CGM</h1>
            <p className="register-subtitle">Créer un nouveau compte</p>
          </div>

          {/* Messages d'erreur et de succès */}
          {error && (
            <div className="error-alert">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
            </div>
          )}
          {success && (
            <div className="success-alert">
              <i className="fas fa-check-circle me-2"></i>
              {success}
            </div>
          )}

          {/* Formulaire d'inscription */}
          <form onSubmit={handleRegister}>
            <div className="form-row">
              {/* Prénom */}
              <div className="modern-input-group">
                <i className="fas fa-user modern-input-icon"></i>
                <input
                  type="text"
                  className="modern-input"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  placeholder="Prénom"
                />
              </div>

              {/* Nom */}
              <div className="modern-input-group">
                <i className="fas fa-user modern-input-icon"></i>
                <input
                  type="text"
                  className="modern-input"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  placeholder="Nom"
                />
              </div>
            </div>

            <div className="form-row">

              {/* Date de naissance */}
              <div className="modern-input-group">
                <i className="fas fa-calendar modern-input-icon"></i>
                <input
                  type="date"
                  className="modern-input"
                  name="date_of_birth"
                  value={formData.date_of_birth}
                  onChange={handleInputChange}
                  placeholder="Date de naissance"
                />
              </div>
            </div>

            <div className="form-row">
              {/* Email */}
              <div className="modern-input-group">
                <i className="fas fa-envelope modern-input-icon"></i>
                <input
                  type="email"
                  className="modern-input"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Email *"
                  required
                />
              </div>

              {/* Téléphone */}
              <div className="modern-input-group">
                <i className="fas fa-phone modern-input-icon"></i>
                <input
                  type="text"
                  className="modern-input"
                  name="phone_number"
                  value={formData.phone_number}
                  onChange={handleInputChange}
                  placeholder="Téléphone"
                />
              </div>
            </div>

            <div className="form-row">
              {/* Mot de passe */}
              <div className="modern-input-group">
                <i className="fas fa-lock modern-input-icon"></i>
                <input
                  type="password"
                  className="modern-input"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Mot de passe * (min 8 caractères)"
                  required
                  minLength={8}
                />
              </div>

              {/* Confirmer mot de passe */}
              <div className="modern-input-group">
                <i className="fas fa-lock modern-input-icon"></i>
                <input
                  type="password"
                  className="modern-input"
                  name="confirm_password"
                  value={formData.confirm_password}
                  onChange={handleInputChange}
                  placeholder="Confirmer le mot de passe *"
                  required
                  minLength={8}
                />
              </div>
            </div>

            {/* Niveau d'accès */}
            <div className="modern-input-group">
              <i className="fas fa-user-shield modern-input-icon"></i>
              <select
                className="modern-input"
                name="access_level"
                value={formData.access_level}
                onChange={handleInputChange}
                required
                style={{ paddingLeft: '40px' }}
              >
                <option value="User">User (Caissier)</option>
                <option value="Admin">Admin</option>
                <option value="SuperAdmin">SuperAdmin</option>
              </select>
            </div>

            {/* Section Username */}
            <div style={{ 
              background: '#f8f9fa', 
              padding: '1rem', 
              borderRadius: '6px', 
              marginBottom: '1rem',
              border: '1px solid #dee2e6'
            }}>
              <label style={{ 
                display: 'block', 
                fontWeight: '600', 
                marginBottom: '0.75rem',
                color: '#495057',
                fontSize: '0.9rem'
              }}>
                <i className="fas fa-user-circle me-2"></i>
                Nom d'utilisateur
              </label>
              
              {/* Prévisualisation du username */}
              {usernameMode === 'auto' && previewUsername && (
                <div style={{ 
                  background: '#e7f3ff', 
                  padding: '0.75rem', 
                  borderRadius: '6px', 
                  marginBottom: '1rem',
                  fontSize: '0.9rem',
                  color: '#0b5796',
                  fontWeight: '500'
                }}>
                  <i className="fas fa-eye me-2"></i>
                  Username prévisualisé : <strong>{previewUsername}</strong>
                </div>
              )}

              {/* Boutons radio */}
              <div style={{ 
                display: 'flex', 
                gap: '1.5rem', 
                marginBottom: usernameMode === 'manual' ? '1rem' : '0',
                flexWrap: 'wrap'
              }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  cursor: 'pointer',
                  fontSize: '0.9rem'
                }}>
                  <input
                    type="radio"
                    name="usernameMode"
                    value="auto"
                    checked={usernameMode === 'auto'}
                    onChange={(e) => {
                      setUsernameMode(e.target.value);
                      setManualUsername('');
                    }}
                    style={{ marginRight: '0.5rem', cursor: 'pointer' }}
                  />
                  <i className="fas fa-magic me-2" style={{ color: '#0b5796' }}></i>
                  Génération automatique
                </label>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  cursor: 'pointer',
                  fontSize: '0.9rem'
                }}>
                  <input
                    type="radio"
                    name="usernameMode"
                    value="manual"
                    checked={usernameMode === 'manual'}
                    onChange={(e) => setUsernameMode(e.target.value)}
                    style={{ marginRight: '0.5rem', cursor: 'pointer' }}
                  />
                  <i className="fas fa-edit me-2" style={{ color: '#0b5796' }}></i>
                  Saisie manuelle
                </label>
              </div>

              {/* Champ username manuel */}
              {usernameMode === 'manual' && (
                <div className="modern-input-group">
                  <i className="fas fa-user modern-input-icon"></i>
                  <input
                    type="text"
                    className="modern-input"
                    value={manualUsername}
                    onChange={(e) => setManualUsername(e.target.value)}
                    placeholder="Saisir le nom d'utilisateur *"
                    required={usernameMode === 'manual'}
                    minLength={3}
                  />
                </div>
              )}
            </div>

            {/* Bouton d'inscription */}
            <button
              type="submit"
              className="modern-register-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="modern-spinner"></div>
                  Inscription en cours...
                </>
              ) : (
                <>
                  <i className="fas fa-user-plus me-2"></i>
                  S'inscrire
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="register-footer">
            <p>Vous avez déjà un compte ? <Link to="/login">Se connecter</Link></p>
          </div>
        </div>
      </div>
    </>
  );
}

